import {
  type HeadlineResponsePersonBaseRelatedPagedData,
  type ImageResponsePagedData,
  type PersonConnectionFullResponse,
  type PersonContactInfoResponse,
  type PersonEducationResponse,
  type PersonInfluencerResponse,
  PersonStatusTier,
  type PersonValuationResponse,
  type PlayerOrgRatingAllResponsePagedData,
  type PlayerOrgResponse,
  type PlayerProfileResponse,
  type PlayerRatingResponse,
  type PlayerVisitsRes,
  type ProfileOrgResponse,
  type ProfileRecruitResponse,
  type PublicMeasurementAveragesResponseRecordMeasurementComparisonRelatedResponseRelatedPagedData,
  type PublicMeasurementPersonResponseRecord,
  type PublicPersonContactInfoResponse,
  type PublicPlayerDealResponsePagedData,
  type RecruitmentEvaluationResponsePagedData,
  type TeamTargetResponsePagedData,
  type ValuationGrowthResponse,
  type VideoResponsePagedData,
} from '@on3/api';
import { type IGenericPageProps } from '@on3/ui-lib/api/schema/custom-contracts';
import { withAuthentication } from '@on3/ui-lib/src/hocs/withAuthentication';
import { withValidUrl } from '@on3/ui-lib/src/hocs/withValidUrl';
import {
  getFulfilledValue,
  proxyApi,
  proxyApiWithVary,
} from '@on3/ui-lib/utils/api';
import { userHasPermission } from '@on3/ui-lib/utils/auth/access';
import { rdb1Level } from '@on3/ui-lib/utils/auth/constants';
import { getIdFromParam, getSportKeyFromParam } from '@on3/ui-lib/utils/url';
import { AthleteClaimModal } from 'components/Db/Profile/AthleteClaim/AthleteClaimModal';
import { PlayerProfile } from 'components/Db/Profile/PlayerProfile';
import { SecondaryPlayerProfile } from 'components/Db/Profile/SecondaryPlayerProfile';
import { PageWrapper } from 'components/Layouts/PageWrapper';
import { ModalProvider } from 'components/Modals';
import { AthleteProvider } from 'contexts/AthleteProvider';
import type { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { createDescription, createTitle } from 'utils/meta';
import {
  type Athlete,
  AthleteType,
  getAthleteType,
  getPlayerProfile,
  getSportKeyFromPage,
  type IPlayerProfile,
  playerModules as modules,
} from 'utils/player';
import { getAthleteData } from 'utils/player/db';

export interface PlayerProfilePageProps extends IGenericPageProps {
  player: PlayerProfileResponse;
  playerProfile: IPlayerProfile;
  playerType: AthleteType;
  academics?: PersonEducationResponse | null;
  college?: ProfileOrgResponse | null;
  contacts?: PublicPersonContactInfoResponse | PersonContactInfoResponse | null;
  nilDeals?: PublicPlayerDealResponsePagedData | null;
  nilGrowth?: ValuationGrowthResponse | null;
  nilValuation?: PersonValuationResponse | null;
  news?: HeadlineResponsePersonBaseRelatedPagedData | null;
  photos?: ImageResponsePagedData | null;
  rankingsAll?: PlayerOrgRatingAllResponsePagedData | null;
  rankingsPlayer?: PlayerRatingResponse | null;
  scoutingJournals?: RecruitmentEvaluationResponsePagedData | null;
  social?: PersonInfluencerResponse[] | null;
  recruit?: ProfileRecruitResponse | null;
  remindsUs?: PersonConnectionFullResponse | null;
  topTeams?: TeamTargetResponsePagedData | null;
  topInterests?: TeamTargetResponsePagedData | null;
  videos?: VideoResponsePagedData | null;
  visits?: PlayerVisitsRes | null;
  vitalAverages?: PublicMeasurementAveragesResponseRecordMeasurementComparisonRelatedResponseRelatedPagedData | null;
  vitals?: PublicMeasurementPersonResponseRecord | null;
}

const PlayerProfilePage = (props: PlayerProfilePageProps) => {
  const data = useMemo(() => {
    return {
      athleteData: props.player,
      socialData: props.social,
      contactData: props.contacts,
    };
  }, [props.player, props.social, props.contacts]);

  const { query } = useRouter();
  const { sport } = query;
  const keySport = (sport as string) ?? 'default';
  const { slug } = props.player || {};

  const isJuniorCollege = props.playerType === AthleteType.Juco;
  const isInCollege = props.playerType === AthleteType.College;
  const isTransfer = props.playerType === AthleteType.Transfer;
  const isInPro = props.playerType === AthleteType.Pro;
  const currentTeam = props.playerProfile?.currentPlayerTeam;

  // Determine organization name based on player type
  let orgName = currentTeam?.organization?.fullName ?? '';

  if (!isJuniorCollege && !isInCollege && !isInPro && !isTransfer)
    orgName = props.player?.highSchoolName ?? '';

  const title = createTitle(
    props.player?.name,
    '-',
    orgName,
    '-',
    props.player?.primaryPosition?.name,
  );

  const description = createDescription(
    `Explore {0}'s athlete profile at On3.com. Get news and insights into his playing career, statistics, and achievements in sports.`,
    props.player?.name,
  );

  const image = useMemo(() => {
    return {
      height: props.player?.defaultAsset?.height || 200,
      src:
        props.player?.defaultAsset &&
        `https://${props?.player?.defaultAsset?.domain}${props.player?.defaultAsset?.source}`,
      width: props.player?.defaultAsset?.width || 200,
    };
  }, [props.player?.defaultAsset]);

  // Football and Basketball are primary sports
  const showPrimaryProfile =
    props.player.tier === PersonStatusTier.Base ||
    [1, 2, 5, 12].includes(props.player?.defaultSport?.key as number);

  return (
    <AthleteProvider data={data}>
      <ModalProvider>
        <AthleteClaimModal />
        <PageWrapper
          {...props}
          canonical={slug && `https://www.on3.com/rivals/${slug}/`}
          description={description}
          image={image}
          ogType="athlete"
          title={title}
        >
          {showPrimaryProfile ? (
            <PlayerProfile {...props} key={keySport} />
          ) : (
            <SecondaryPlayerProfile {...props} />
          )}
        </PageWrapper>
      </ModalProvider>
    </AthleteProvider>
  );
};

export const getServerSideProps: GetServerSideProps<
  PlayerProfilePageProps | { notFound: boolean }
> = async (ctx) => {
  const siteData = await withValidUrl.isValid(ctx);
  const userData = await withAuthentication.isAuth(ctx);
  const { requestWithVaryParams } = userData || {};

  if (!siteData) return { notFound: true };

  const { id, sport } = ctx.query as { id: string; sport?: string };

  const playerId = getIdFromParam(id);

  if (!playerId) return { notFound: true };

  const personKey = +playerId;
  // Get sport key from query param (if specified)
  let sportKey = getSportKeyFromParam(sport?.toLowerCase() ?? '') ?? undefined;

  try {
    const [player] = await Promise.allSettled([
      modules.player(proxyApi, { personKey, sportKey }),
    ]);

    const fetchedPlayer = getFulfilledValue(player);

    // If player is not found, return 404
    if (!fetchedPlayer) return { notFound: true };

    //This will ensure we are always are on the correct url and there won't be any duplication.
    if (id !== fetchedPlayer.slug) {
      return {
        redirect: {
          permanent: true,
          destination: `/rivals/${fetchedPlayer.slug}/`,
        },
      };
    }

    // If person is a coach, redirect to coach page
    if (!fetchedPlayer.isAthlete && fetchedPlayer.isCoach) {
      return {
        redirect: {
          permanent: true,
          destination: `/rivals/coach/${fetchedPlayer.slug}/`,
        },
      };
    }

    // Set official player sport key
    sportKey = getSportKeyFromPage(fetchedPlayer, sport);
    const isPrimaryProfile =
      fetchedPlayer.tier === PersonStatusTier.Base ||
      [1, 2, 5, 12].includes(fetchedPlayer?.defaultSport?.key as number);

    const playerType = getAthleteType(fetchedPlayer as Athlete);

    // Determine if we should use internal or external endpoints for:
    // - contacts
    const contactPrivacy = userHasPermission(rdb1Level, userData)
      ? 'private'
      : 'public';

    const data = {
      pageType: 'database',
      siteData,
      userData,
      player: fetchedPlayer,
      playerType,
    } as PlayerProfilePageProps;

    const playerKeys = {
      personKey,
      orgKey: fetchedPlayer.collegeOrgKey || 0,
      recruitKey: fetchedPlayer.recruitmentKey || 0,
      sportKey,
      currentRecKey: fetchedPlayer.playerStatus?.recruitmentKey || 0,
    };

    // Fetch data for the player profile
    type Promises = {
      [K in keyof PlayerProfilePageProps]: () => Promise<
        PlayerProfilePageProps[K]
      >;
    } & {
      organizations: () => Promise<PlayerOrgResponse>;
    };

    let promises = {
      organizations: () =>
        modules.organizations(proxyApi, fetchedPlayer.personSportKey as number),
    } as Promises;

    // Fetch data based on player type
    if (isPrimaryProfile) {
      promises = {
        ...promises,
        contacts: () =>
          modules.contacts[contactPrivacy](
            proxyApiWithVary,
            personKey,
            requestWithVaryParams,
          ),
        news: () => modules.news(proxyApi, { key: personKey, limit: 5 }),
        nilValuation: () =>
          modules.nil.valuation(
            proxyApiWithVary,
            personKey,
            requestWithVaryParams,
          ),
        photos: () => modules.photos(proxyApi, { personKey }),
        rankingsPlayer: () =>
          modules.rankings.player(proxyApi, {
            personKey,
            sportKey,
          }),
        remindsUs: () => modules.reminds(proxyApi, personKey),
      };

      const playerTypePromises = getAthleteData(playerType, playerKeys);

      promises = { ...promises, ...playerTypePromises };
    } else {
      promises = {
        ...promises,
        contacts: () =>
          modules.contacts[contactPrivacy](
            proxyApiWithVary,
            personKey,
            requestWithVaryParams,
          ),
        nilDeals: () => modules.nil.deals(proxyApi, { personKey }),
        nilGrowth: () => modules.nil.growth(proxyApi, { personKey }),
        nilValuation: () =>
          modules.nil.valuation(
            proxyApiWithVary,
            personKey,
            requestWithVaryParams,
          ),
        news: () => modules.news(proxyApi, { key: personKey, limit: 5 }),
        photos: () => modules.photos(proxyApi, { personKey }),
        rankingsPlayer: () =>
          modules.rankings.player(proxyApi, {
            personKey,
            sportKey,
          }),
        recruit: () => modules.recruit(proxyApi, personKey),
        social: () => modules.social(proxyApi, personKey),
      };

      if (playerType === AthleteType.College) {
        promises.college = () =>
          modules.college(proxyApi, {
            playerKey: personKey,
            orgKey: playerKeys.orgKey,
          });
        promises.remindsUs = () => modules.reminds(proxyApi, personKey);
      }
    }

    // Fetch all player data in parallel here
    const resolvedPromises = await Promise.allSettled(
      Object.values(promises).map((promise) => promise()),
    );

    resolvedPromises.forEach((promise, i) => {
      const key = Object.keys(promises)[i] as keyof Promises;

      if (key === 'organizations') {
        const playerOrganizations = getFulfilledValue(
          promise,
        ) as PlayerOrgResponse | null;
        const playerProfile = getPlayerProfile(playerOrganizations);

        data.playerProfile = playerProfile;
      } else {
        Object.assign(data, { [key]: getFulfilledValue(promise) });
      }
    });

    return {
      props: data,
    };
  } catch (e) {
    console.error(e);

    return {
      props: { notFound: true },
    };
  }
};

export default withValidUrl(PlayerProfilePage);
